import { useMemo } from "react";

/** Small deterministic PRNG — the table must look the same on every render and every phone. */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const UNIT = 100;

/**
 * One wobbly line from a to b: overshoots a little at both ends (nobody stops a pen
 * exactly at the corner), bows through a jittered control point, varies its weight.
 */
function stroke(rand: () => number, x0: number, y0: number, x1: number, y1: number) {
  const j = (amp: number) => (rand() * 2 - 1) * amp;
  const vertical = x0 === x1;
  const over0 = 1 + rand() * 4;
  const over1 = 1 + rand() * 4;
  const sx = vertical ? x0 + j(1.2) : x0 - over0;
  const sy = vertical ? y0 - over0 : y0 + j(1.2);
  const ex = vertical ? x1 + j(1.2) : x1 + over1;
  const ey = vertical ? y1 + over1 : y1 + j(1.2);
  const cx = (sx + ex) / 2 + (vertical ? j(2.6) : j(12));
  const cy = (sy + ey) / 2 + (vertical ? j(12) : j(2.6));
  return {
    d: `M${sx.toFixed(1)} ${sy.toFixed(1)} Q${cx.toFixed(1)} ${cy.toFixed(1)} ${ex.toFixed(1)} ${ey.toFixed(1)}`,
    width: (1.25 + rand() * 0.6).toFixed(2),
    opacity: (0.72 + rand() * 0.28).toFixed(2),
  };
}

/** The dictation table drawn by hand over the cells: (cols + 1) verticals, (rows + 1) horizontals. */
export function HandGrid({ cols, rows }: { cols: number; rows: number }) {
  const lines = useMemo(() => {
    const rand = mulberry32(1574 + cols * 31 + rows);
    const w = cols * UNIT;
    const h = rows * UNIT;
    const out = [];
    for (let c = 0; c <= cols; c++) out.push(stroke(rand, c * UNIT, 0, c * UNIT, h));
    for (let r = 0; r <= rows; r++) out.push(stroke(rand, 0, r * UNIT, w, r * UNIT));
    return out;
  }, [cols, rows]);

  return (
    <svg
      className="nb-hand"
      viewBox={`0 0 ${cols * UNIT} ${rows * UNIT}`}
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      {lines.map((l, i) => (
        <path key={i} d={l.d} strokeWidth={l.width} opacity={l.opacity} vectorEffect="non-scaling-stroke" />
      ))}
    </svg>
  );
}
